import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, GraduationCap, MapPin, Plane, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const contentVariants = {
  hidden: { opacity: 0, y: 24, scale: 0.98 },
  show:   { opacity: 1, y: 0,  scale: 1,    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit:   { opacity: 0, y: 12, scale: 0.98, transition: { duration: 0.2 } },
};

const listStagger = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
};
const itemFade = {
  hidden: { opacity: 0, y: 14 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

export const CountryDetailsModal = ({ open, onOpenChange, country, onBookConsultation }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 gap-0 border-none bg-transparent shadow-none rounded-none max-h-[92vh] overflow-visible"
        data-testid="country-details-modal"
      >
        <DialogTitle className="sr-only">{country?.name || "Country"} details</DialogTitle>
        <DialogDescription className="sr-only">Overview, facilities and top universities for {country?.name || "this country"}.</DialogDescription>

        <AnimatePresence>
          {country && (
            <motion.div
              key={country.code}
              variants={contentVariants}
              initial="hidden" animate="show" exit="exit"
              className="relative rounded-[2rem] bg-white overflow-hidden max-h-[92vh] flex flex-col shadow-[0_40px_120px_-20px_rgba(217,4,41,0.35)]"
            >
              {/* Header */}
              <div className="relative px-8 py-7 bg-brand-red text-white overflow-hidden shrink-0">
                <div className="absolute -top-20 -right-16 w-52 h-52 rounded-full bg-white/10" />
                <div className="absolute -bottom-24 -left-8 w-52 h-52 rounded-full bg-white/5" />
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage: "radial-gradient(circle at 30% 30%, #fff 1px, transparent 1px)",
                    backgroundSize: "22px 22px",
                  }}
                />

                <div className="relative flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <motion.div
                      initial={{ rotate: -12, scale: 0.7 }} animate={{ rotate: 0, scale: 1 }}
                      transition={{ delay: 0.15, type: "spring", stiffness: 180 }}
                      className="text-[64px] leading-none"
                    >
                      {country.flag}
                    </motion.div>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-bold">Study Abroad Guide</div>
                      <div className="font-heading font-black text-3xl md:text-4xl leading-tight">{country.name}</div>
                      <div className="mt-1 text-white/85 text-sm truncate">{country.tag}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => onOpenChange(false)}
                    data-testid="country-modal-close"
                    aria-label="Close"
                    className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 backdrop-blur-sm grid place-items-center transition shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="scrollbar-hidden overflow-y-auto flex-1 px-8 py-8 space-y-10">
                {/* Overview */}
                <motion.section variants={itemFade} initial="hidden" animate="show" data-testid="country-modal-overview">
                  <h3 className="font-heading font-black text-xl text-brand-navy flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-brand-red" /> Overview
                  </h3>
                  <p className="mt-3 text-brand-navy/70 leading-relaxed text-[15px]">
                    {country.description}
                  </p>
                </motion.section>

                {/* Facilities */}
                <section data-testid="country-modal-facilities">
                  <h3 className="font-heading font-black text-xl text-brand-navy flex items-center gap-2">
                    <Plane className="w-5 h-5 text-brand-red" /> Facilities we provide
                  </h3>
                  <motion.div
                    variants={listStagger} initial="hidden" animate="show"
                    className="mt-4 grid sm:grid-cols-2 gap-3"
                  >
                    {(country.facilities || []).map((f) => (
                      <motion.div
                        key={f}
                        variants={itemFade}
                        data-testid={`country-facility-${f.slice(0, 12).toLowerCase().replace(/\s+/g, "-")}`}
                        className="flex items-center gap-3 p-3.5 rounded-2xl bg-emerald-50 border border-emerald-500/20"
                      >
                        <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0" strokeWidth={2.25} />
                        <div className="text-sm text-brand-navy font-semibold leading-snug">{f}</div>
                      </motion.div>
                    ))}
                  </motion.div>
                </section>

                {/* Top 10 Universities */}
                {(country.universities || []).length > 0 && (
                <section data-testid="country-modal-universities">
                  <div className="flex items-end justify-between gap-3">
                    <h3 className="font-heading font-black text-xl text-brand-navy flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-brand-red" /> Top 10 Universities
                    </h3>
                    <span className="text-xs text-brand-navy/50">Ranked · Latest QS + Times</span>
                  </div>
                  <motion.div
                    variants={listStagger} initial="hidden" animate="show"
                    className="mt-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                  >
                    {country.universities.map((u, i) => (
                      <motion.div
                        key={u.name}
                        variants={itemFade}
                        data-testid={`university-card-${i}`}
                        className="group flex items-center gap-3 p-3 rounded-2xl border border-brand-navy/10 hover:border-brand-red/40 hover:shadow-md transition-all bg-white"
                      >
                        <div className="relative shrink-0">
                          <img
                            src={u.logo}
                            alt={u.name}
                            loading="lazy"
                            className="w-12 h-12 rounded-xl object-cover ring-1 ring-brand-navy/10 group-hover:ring-brand-red/40 transition"
                          />
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-brand-red text-white text-[10px] font-black grid place-items-center ring-2 ring-white">
                            {i + 1}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="text-[13px] font-bold text-brand-navy leading-tight line-clamp-2">{u.name}</div>
                          <div className="text-[10px] text-brand-navy/50 mt-0.5 uppercase tracking-widest">{country.name}</div>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </section>
                )}
              </div>

              {/* Footer CTA */}
              <div className="shrink-0 px-8 py-5 border-t border-brand-navy/10 bg-white flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="text-sm text-brand-navy/60 text-center sm:text-left">
                  Ready to make <span className="font-semibold text-brand-navy">{country.name}</span> home?
                </div>
                <Button
                  data-testid="country-modal-book-cta"
                  onClick={() => { onOpenChange(false); onBookConsultation?.(country); }}
                  className="rounded-full bg-brand-red hover:bg-brand-red-hover text-white font-semibold px-6 h-11 brand-shadow btn-scale"
                >
                  Book Free Consultation <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
